import { FC, ReactNode, useMemo, useState } from 'react';

import { FormikHelpers } from 'formik';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Typography from '@mui/material/Typography';

import { getSpecialDropFormInitialValues, SpecialDropFormValues } from './form';
import { SpecialDropEditModal } from './specialDropEditModal/SpecialDropEditModal';
import { SpecialDropForm } from './specialDropForm/SpecialDropForm';
import { SpecialDropsTable } from './specialDropsTable/SpecialDropsTable';
import { getMultipliedRewardsPayload } from './utils';

import css from './SpecialDropsSection.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { specialDropService } from 'v2/api/specialDropService';
import { SpecialDropType } from 'v2/types/SpecialDropModel';
import { handleHttpError } from 'v2/utils';

interface SpecialDropsSectionProps {
  type: SpecialDropType;
  title?: string;
  note?: ReactNode;
}

export const SpecialDropsSection: FC<SpecialDropsSectionProps> = ({ type, title = 'Special Drops', note }) => {
  const [currentSpecialDropId, setCurrentSpecialDropId] = useState<string>('');
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);

  const { data: specialDrops = [], isLoading, mutate } = useSWR('getSpecialDrops', specialDropService.getSpecialDrops);

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerCreateSpecialDrop, isMutating: isCreatingSpecialDrop } = useSWRMutation(
    'createSpecialDrop',
    specialDropService.createSpecialDrop
  );

  const { trigger: triggerDeleteSpecialDrop } = useSWRMutation('deleteSpecialDrop', (_key, { arg }: { arg: string }) =>
    specialDropService.deleteSpecialDrop(['deleteSpecialDrop', arg])
  );

  const filteredSpecialDrops = useMemo(
    () => specialDrops.filter((specialDrop) => specialDrop.type === type),
    [specialDrops, type]
  );

  const handleCreateSpecialDrop = async (
    formValues: SpecialDropFormValues,
    { resetForm }: FormikHelpers<SpecialDropFormValues>
  ) => {
    try {
      const rewardsPayload = getMultipliedRewardsPayload(formValues.rewards);
      const createdRewardIds = await triggerCreateRewards(rewardsPayload);

      await triggerCreateSpecialDrop({
        chanceToWin: formValues.chanceToWin,
        type: formValues.type,
        rewardIds: createdRewardIds,
      });

      toast.success('Special drop has been successfully created');
      await mutate();
      resetForm();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleDeleteSpecialDrop = async (specialDropId: string) => {
    try {
      await triggerDeleteSpecialDrop(specialDropId);
      toast.success('Special drop has been successfully deleted');
      await mutate();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleEditModalOpen = (specialDropId: string) => {
    setCurrentSpecialDropId(specialDropId);
    setIsEditModalOpened(true);
  };

  const handleEditModalClose = () => {
    setCurrentSpecialDropId('');
    setIsEditModalOpened(false);
  };

  const currentSpecialDrop = filteredSpecialDrops.find((drop) => drop.id === currentSpecialDropId) ?? null;
  const isProcessing = isCreatingRewards || isCreatingSpecialDrop;

  return (
    <div className={css.specialDropsSection}>
      <Typography fontSize="24px" fontWeight={600}>
        {title}
      </Typography>

      {note}

      <div className={css.specialDropsLayout}>
        <div className={css.specialDropsFormColumn}>
          <Typography fontSize="18px" fontWeight={600}>
            Create Special Drop
          </Typography>

          <SpecialDropForm
            submitButtonCaption="Create"
            initialValues={getSpecialDropFormInitialValues(type)}
            isSubmitting={isProcessing}
            onSubmit={handleCreateSpecialDrop}
          />
        </div>

        <div className={css.specialDropsListColumn}>
          <Typography fontSize="18px" fontWeight={600}>
            All Special Drops
          </Typography>

          {!isLoading && (
            <SpecialDropsTable
              specialDrops={filteredSpecialDrops}
              onEdit={handleEditModalOpen}
              onDelete={handleDeleteSpecialDrop}
            />
          )}
        </div>
      </div>

      <SpecialDropEditModal
        specialDrop={currentSpecialDrop}
        isOpened={isEditModalOpened}
        handleClose={handleEditModalClose}
        onRevalidate={mutate}
      />
    </div>
  );
};
